import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { getSymptom, likelyStage } from '../../data/symptoms';
import { getStage } from '../../data/stages';
import { getCarePath } from '../../data/carePaths';
interface AnswerPanelProps {
  selected: string[];
  /** The teaser shows the first two only; the full decoder shows everything. */
  limit?: number;
}
export function AnswerPanel({
  selected,
  limit
}: AnswerPanelProps) {
  const stageId = likelyStage(selected);
  const stage = stageId ? getStage(stageId) : undefined;
  const picked = selected.map(getSymptom).filter(Boolean);
  const shown = limit ? picked.slice(0, limit) : picked;
  const dismissedCount = picked.filter(symptom => symptom?.commonlyDismissed).length;
  const carePathSlug = shown[0]?.carePath;
  const carePath = carePathSlug ? getCarePath(carePathSlug) : undefined;
  return <motion.div initial={{
    opacity: 0,
    y: 4
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} aria-live="polite" className="rounded-brand border border-ink/15 bg-cream-light p-7 md:p-10" data-id="element-73" data-mp="36p8972r5jq2w">
      {stage && <>
          <p className="text-[0.6875rem] uppercase tracking-label text-brick" data-id="element-74" data-mp="a0wn9z316rrh">
            What this pattern points to
          </p>
          <h3 className="mt-4 font-serif text-3xl leading-tight text-ink md:text-4xl" data-id="element-75" data-mp="51a6xgrwoveg">
            {stage.name}
            <span className="text-ink-muted" data-id="element-76" data-mp="39hbe3gp0rwp3"> · {stage.ages}</span>
          </h3>
          <p className="mt-4 max-w-2xl leading-relaxed text-ink-soft" data-id="element-77" data-mp="39f8eq61z8r7p">{stage.physiology}</p>
          {dismissedCount > 0 && <p className="mt-4 max-w-2xl leading-relaxed text-ink" data-id="element-78" data-mp="3ii9hbwrmw06m">
              {dismissedCount === 1 ? 'One of the things you selected is among the symptoms women are most often told is nothing.' : `${dismissedCount} of the things you selected are among the symptoms women are most often told are nothing.`}{' '}
              They are not nothing.
            </p>}
        </>}

      <ul className="mt-9 flex flex-col" data-id="element-79" data-mp="yo8u78wy64k2">
        {shown.map(symptom => <li key={symptom!.id} className="rule py-6 first:border-t-0 first:pt-0" data-id="element-80" data-mp="13fh4s8a0jvyt">
            <h4 className="font-serif text-xl leading-snug text-ink" data-id="element-81" data-mp="18eu0zjmryfe2">{symptom!.label}</h4>
            <div className="mt-4 grid gap-5 md:grid-cols-2 md:gap-10" data-id="element-82" data-mp="arbhpevhluvt">
              <div data-id="element-83" data-mp="at628s2ujkzd">
                <p className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-84" data-mp="ato547k0kxg9">
                  What’s happening
                </p>
                <p className="mt-2 text-[0.9375rem] leading-relaxed text-ink-soft" data-id="element-85" data-mp="kog51i12jwe7">
                  {symptom!.whatsHappening}
                </p>
              </div>
              <div data-id="element-86" data-mp="1w4y31mkbe72y">
                <p className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-87" data-mp="1w5fwkbfle776">
                  What usually helps
                </p>
                <p className="mt-2 text-[0.9375rem] leading-relaxed text-ink-soft" data-id="element-88" data-mp="208ck8gjzothz">
                  {symptom!.whatHelps}
                </p>
              </div>
            </div>
          </li>)}
      </ul>

      {limit && picked.length > limit && <p className="rule pt-6 text-sm text-ink-soft" data-id="element-89" data-mp="3buynpaqmpoqr">
          {picked.length - limit} more of your selections are explained in the full decoder.
        </p>}

      {carePath && <div className="rule-light mt-8 pt-8" data-id="element-90" data-mp="31y2ojvmmeeyv">
          <Link to={`/care/${carePath.slug}`} className="group inline-flex items-center gap-2 font-serif text-xl text-brick transition-colors duration-150 ease-brand hover:text-brick-deep" data-id="element-91" data-mp="31vpwgpn5eo83">
            The care path for this: {carePath.name}
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-brand group-hover:translate-x-1" aria-hidden="true" data-id="element-92" data-mp="2nzm7bar4t62h" />
          </Link>
        </div>}
    </motion.div>;
}